import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import color from "../../../Constants/colors";
import { CardTitle, cardSx } from "../../HomePage/DashboardCard";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { RegistrationStatus, SalesStatus } from "../../../Models/ambassador";
import { useAmbassadorLabels } from "./useAmbassadorLabels";

export const PageHeader: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, subtitle, actions, children }) => {
  const { primary } = useDashboardColors();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: 26,
            fontWeight: 800,
            lineHeight: "34px",
            color: primary,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ mt: 0.5, color: color.gray500, fontSize: 14 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </Box>
      {actions && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

/** White card with the accent-bar title of the home dashboard. */
export const Panel: React.FC<{
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, subtitle, action, children, sx }) => (
  <Box
    component="section"
    sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, ...sx } as SxProps<Theme>}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        {title ? <CardTitle title={title} subtitle={subtitle} /> : <span />}
        {action}
      </Box>
    )}
    {children}
  </Box>
);

export const StatTile: React.FC<{
  label: string;
  value: number | undefined;
  hint?: string;
  highlight?: boolean;
}> = ({ label, value, hint, highlight }) => {
  const { primary, accent } = useDashboardColors();
  return (
    <Box
      sx={{
        ...cardSx,
        p: 2,
        borderTop: highlight ? `3px solid ${accent}` : cardSx.border,
      }}
    >
      <Typography sx={{ fontSize: 12, color: color.gray500, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: primary }}>
          {value === undefined ? "–" : String(value).padStart(2, "0")}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 12, color: color.gray400 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const StatGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        md: "repeat(4, minmax(0, 1fr))",
      },
      gap: 2,
      mb: 3,
    }}
  >
    {children}
  </Box>
);

type Tone = "gray" | "blue" | "orange" | "green" | "red";

const TONES: Record<Tone, { bg: string; text: string; dot: string }> = {
  gray: { bg: color.gray100, text: color.gray700, dot: color.gray400 },
  blue: { bg: color.blue50, text: color.blue700, dot: color.blue700 },
  orange: { bg: color.orange50, text: color.orange600, dot: color.orange500 },
  green: { bg: color.green50, text: color.green700, dot: color.green700 },
  red: { bg: color.red50, text: color.red700, dot: color.red600 },
};

export const StatusChip: React.FC<{ label: string; tone: Tone }> = ({
  label,
  tone,
}) => {
  const style = TONES[tone];
  return (
    <Chip
      size="small"
      label={
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: style.dot,
            }}
          />
          {label}
        </Box>
      }
      sx={{
        height: 24,
        borderRadius: "12px",
        fontWeight: 700,
        backgroundColor: style.bg,
        color: style.text,
        "& .MuiChip-label": { px: 1.1, fontSize: 12 },
      }}
    />
  );
};

const REGISTRATION_TONES: Record<RegistrationStatus, Tone> = {
  [RegistrationStatus.DRAFT]: "gray",
  [RegistrationStatus.SUBMITTED]: "blue",
  [RegistrationStatus.UNDER_REVIEW]: "orange",
  [RegistrationStatus.QUESTIONS_PENDING]: "orange",
  [RegistrationStatus.CONFIRMED]: "green",
  [RegistrationStatus.REJECTED]: "red",
  [RegistrationStatus.WITHDRAWN]: "gray",
};

export const RegistrationStatusChip: React.FC<{
  status: RegistrationStatus;
}> = ({ status }) => {
  const labels = useAmbassadorLabels();
  return (
    <StatusChip
      label={labels.registrationStatus(status)}
      tone={REGISTRATION_TONES[status] ?? "gray"}
    />
  );
};

export const SalesStatusChip: React.FC<{ status: SalesStatus | null }> = ({
  status,
}) => {
  const labels = useAmbassadorLabels();
  if (status === null) {
    return (
      <Typography sx={{ color: color.gray400, fontSize: 14 }}>–</Typography>
    );
  }
  const tone: Tone =
    status === SalesStatus.WON
      ? "green"
      : status === SalesStatus.LOST
        ? "red"
        : "blue";
  return <StatusChip label={labels.salesStatus(status)} tone={tone} />;
};

export const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "180px minmax(0, 1fr)" },
      gap: { xs: 0.25, sm: 2 },
      py: 1,
      borderBottom: `1px solid ${color.gray100}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography sx={{ fontSize: 13, color: color.gray500 }}>{label}</Typography>
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: color.gray900,
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {value === "" ||
      value === null ||
      value === undefined ||
      value === false ? (
        <Typography
          component="span"
          sx={{ color: color.gray400, fontSize: 14 }}
        >
          –
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  text?: string;
  action?: React.ReactNode;
}> = ({ icon, title, text, action }) => (
  <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
    {icon && (
      <Box sx={{ color: color.gray300, mb: 1, "& svg": { fontSize: 40 } }}>
        {icon}
      </Box>
    )}
    <Typography sx={{ fontWeight: 700, color: color.gray700 }}>
      {title}
    </Typography>
    {text && (
      <Typography sx={{ mt: 0.5, color: color.gray500, fontSize: 14 }}>
        {text}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
);

export const LoadingState: React.FC = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress size={28} />
  </Box>
);

export const ErrorState: React.FC<{ onRetry?: () => void; text?: string }> = ({
  onRetry,
  text,
}) => {
  const { t } = useTranslation("ambassador");
  return (
    <Alert
      severity="error"
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t("common.retry")}
          </Button>
        )
      }
    >
      {text ?? t("common.loadError")}
    </Alert>
  );
};
